import axios, { AxiosError, type AxiosInstance } from 'axios';
import { sessionExpiredStore } from './sessionExpiry';

export interface ApiClientOptions {
  baseURL: string;
  /**
   * Identifies which frontend app this client belongs to.
   * Sent as {@code X-Ota-App} on every request so the backend can issue and
   * read an app-specific HttpOnly cookie, preventing sessions from bleeding
   * across ota-public, ota-internal, and ota-admin.
   */
  appId: 'public' | 'internal' | 'admin';
  /**
   * Optional legacy callback for 401 responses. Prefer mounting
   * {@code <SessionExpiredDialog/>} at the app root and letting the shared
   * {@code sessionExpiredStore} drive the redirect — that pattern gives the
   * user a clear "session expired" message instead of an unexplained
   * navigation back to login.
   */
  onUnauthenticated?: () => void;
}

export interface ApiErrorBody {
  timestamp?: string;
  status?: number;
  error?: string;
  message?: string;
  fieldErrors?: Record<string, string>;
}

export class ApiError extends Error {
  status: number;
  body?: ApiErrorBody;
  constructor(message: string, status: number, body?: ApiErrorBody) {
    super(message);
    this.status = status;
    this.body = body;
  }
}

/**
 * Builds a configured Axios instance shared by all three apps.
 *
 * Authentication is carried by an HttpOnly cookie — the client never reads
 * or attaches a token. {@code withCredentials: true} ensures the browser
 * includes the cookie on every cross-origin request to the same API.
 */
export function createApiClient(options: ApiClientOptions): AxiosInstance {
  const instance = axios.create({
    baseURL: options.baseURL,
    timeout: 80000,
    withCredentials: true,  // send the HttpOnly auth cookie automatically
    headers: { 'X-Ota-App': options.appId },
  });

  instance.interceptors.response.use(
    (resp) => resp,
    (error: AxiosError<ApiErrorBody>) => {
      if (error.response?.status === 401) {
        // Fire the shared store FIRST so concurrent 401s on the same page
        // collapse into a single dialog (the store is idempotent). The
        // legacy callback runs after, for apps that haven't migrated to
        // the dialog yet.
        sessionExpiredStore.trigger();
        options.onUnauthenticated?.();
      }
      const body = error.response?.data;
      throw new ApiError(
        body?.message ?? error.message ?? 'Request failed',
        error.response?.status ?? 0,
        body,
      );
    },
  );

  return instance;
}
